import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ConfirmPasswordModal } from '@/components/ConfirmPasswordModal'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Modal } from '@/components/ui/Modal'
import { StatusPill } from '@/components/ui/StatusPill'
import { useToast } from '@/components/ui/toastContext'
import type { RecruiterTeamMember } from '@/features/recruiter/api/team.api'
import {
  useActivateMember,
  useDeactivateMember,
  useInviteTeamMember,
  useTeamQuery,
} from '@/features/recruiter/hooks/useTeam'
import { phoneSchema } from '@/features/auth/schemas'
import { usePasswordConfirmGate } from '@/hooks/usePasswordConfirmGate'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'
import { formatDate, humanizeEnum } from '@/utils/format'

const inviteSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  email: z.email('Enter a valid email address'),
  phone: phoneSchema,
})

type InviteFormValues = z.infer<typeof inviteSchema>

export function TeamManagementPage() {
  const { data: members, isPending } = useTeamQuery()
  const invite = useInviteTeamMember()
  const activate = useActivateMember()
  const deactivate = useDeactivateMember()
  const toast = useToast()
  const gate = usePasswordConfirmGate()
  const [showInvite, setShowInvite] = useState(false)
  const [inviteError, setInviteError] = useState<unknown>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { name: '', email: '', phone: '' },
  })

  const onInvite = handleSubmit((values) => {
    setInviteError(null)
    invite.mutate(values, {
      onSuccess: () => {
        reset()
        setShowInvite(false)
        toast.success('Invitation sent.')
      },
      onError: (error) => {
        if (!gate.guardedError(error, () => void onInvite())) {
          setInviteError(error)
        }
      },
    })
  })

  const toggleStatus = (member: RecruiterTeamMember) => {
    const action = member.membershipStatus === 'active' ? deactivate : activate
    const retry = () => toggleStatus(member)
    action.mutate(member.id, {
      onError: (error) => {
        if (!gate.guardedError(error, retry)) {
          toast.error(getErrorMessage(error))
        }
      },
    })
  }

  const columns: DataTableColumn<RecruiterTeamMember>[] = [
    { key: 'name', header: 'Name', render: (row) => row.name },
    { key: 'email', header: 'Email', render: (row) => row.email },
    { key: 'role', header: 'Role', render: (row) => humanizeEnum(row.userType) },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (row.membershipStatus ? <StatusPill status={row.membershipStatus} /> : '—'),
    },
    { key: 'joined', header: 'Joined', render: (row) => formatDate(row.createdAt) },
    {
      key: 'actions',
      header: '',
      render: (row) =>
        row.isPrimaryAdmin ? (
          <span className="fs-12 text-muted">Primary admin</span>
        ) : (
          <Button
            variant={row.membershipStatus === 'active' ? 'danger' : 'secondary'}
            confirmTitle={
              row.membershipStatus === 'active' ? 'Deactivate this teammate?' : undefined
            }
            skipConfirm={row.membershipStatus !== 'active'}
            onClick={() => toggleStatus(row)}
          >
            {row.membershipStatus === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
        ),
    },
  ]

  return (
    <>
      <div className="my-4 d-flex align-items-center justify-content-between flex-wrap gap-2 page-header-breadcrumb">
        <div>
          <p className="fw-semibold fs-20 mb-0">Team Management</p>
        </div>
        <Button onClick={() => setShowInvite(true)}>Invite Teammate</Button>
      </div>

      <div className="card custom-card">
        <DataTable
          columns={columns}
          rows={members ?? []}
          rowKey={(row) => row.id}
          loading={isPending}
          emptyMessage="No team members yet."
        />
      </div>

      <Modal show={showInvite} onClose={() => setShowInvite(false)} title="Invite Teammate">
        <FormErrorSummary
          errors={errors}
          serverErrors={getFieldErrors(inviteError)}
          generalMessage={inviteError ? getErrorMessage(inviteError) : null}
        />
        <form onSubmit={(e) => void onInvite(e)} noValidate>
          <FormInput control={control} name="name" label="Full Name" required />
          <FormInput control={control} name="email" label="Email" type="email" required />
          <FormInput control={control} name="phone" label="Phone" type="tel" required />
          <Button type="submit" className="w-100" loading={invite.isPending}>
            Send Invitation
          </Button>
        </form>
      </Modal>

      <ConfirmPasswordModal
        show={gate.show}
        onClose={() => gate.setShow(false)}
        onConfirmed={gate.handleConfirmed}
      />
    </>
  )
}
